import { createContext, useCallback, useReducer, useRef } from 'react';
import { getApplications } from '../../api/endpoints/application';
import { isEmpty } from '../../utils/cleanObject';
import { isSubscriptionStatusOk } from '../../utils/stripe_utils';
import { formatDate } from '../../utils/various';

export const StatePositionContext = createContext();
export const DispatchPositionContext = createContext();

// return message based on state of subscription
const setSubMessage = (subscriptionObject, position, t) => {
  if (position?.unsolicited)
    return t('page.position.basic_subscription');
  if (
    !subscriptionObject ||
    isEmpty(subscriptionObject) ||
    !isSubscriptionStatusOk(subscriptionObject.status)
  )
    return t('page.position.no_active_subscription');
  if (
    isSubscriptionStatusOk(subscriptionObject.status) &&
    subscriptionObject.cancel_at
  )
    return `${t('page.position.subscription_will_be_cancelled')} ${formatDate(subscriptionObject.cancel_at * 1000)}`;
  if (isSubscriptionStatusOk(subscriptionObject.status)) {
    return `${t('page.position.subscription_subscribed')} ${formatDate(subscriptionObject.current_period_end * 1000)}`;
  }
};

const positionReducer = (state, action, t) => {
  switch (action.type) {
    case 'update_position_page_site': {
      return {
        ...state,
        single: {
          ...state.single,
          newfieldwithvalue: action.payload,
        }
      }
    }
    case 'init_positions': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'init_single_position': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'view_count': {
      return {
        ...state,
        count: action.payload
      }
    }
    case 'edit_position_view': {
      return {
        ...state,
        editPosition: action.payload
      }
    }
    case 'application_id': {
      return {
        ...state,
        appId: action.payload,
      }
    }
    case 'application_status': {
      return {
        ...state,
        substatus: action.payload,
      }
    }
    case 'init_selected_application': {
      const { id, substatus, preserveCandidateView } = action.payload;
      return {
        ...state,
        appId: id,
        substatus,
        candidateView: preserveCandidateView ? state.candidateView : true,
        selectedApplication: null,
        isLoadingSelectedApplication: true,
      };
    }
    case 'loaded_selected_application': {
      const pendingUpdate =
        state.pendingSelectedApplicationUpdate?.id === action.payload?.id
          ? state.pendingSelectedApplicationUpdate
          : null;

      return {
        ...state,
        selectedApplication: pendingUpdate
          ? {
            ...action.payload,
            ...pendingUpdate,
          }
          : action.payload,
        isLoadingSelectedApplication: false,
        pendingSelectedApplicationUpdate: pendingUpdate ? null : state.pendingSelectedApplicationUpdate,
      };
    }
    case 'update_selected_application': {
      const { id, ...updates } = action.payload;

      return {
        ...state,
        pendingSelectedApplicationUpdate: {
          id,
          ...updates,
        },
        selectedApplication:
          state.selectedApplication?.id === id
            ? {
              ...state.selectedApplication,
              ...updates,
            }
            : state.selectedApplication,
      };
    }
    case 'error_selected_application': {
      return {
        ...state,
        selectedApplication: null,
        isLoadingSelectedApplication: false,
      };
    }
    case 'set_selected_application': {
      return {
        ...state,
        appId: action.payload?.id,
        substatus: action.payload?.substatus ?? state.substatus,
        selectedApplication: action.payload,
        isLoadingSelectedApplication: false,
        candidateView: true,
      };
    }
    case 'refetch_positions': {
      return {
        ...state,
        refetch: action.payload,
      };
    }
    case 'candidate_view': {
      return {
        ...state,
        candidateView: action.payload,
      }
    }
    case 'create_position_modal': {
      return {
        ...state,
        createPositionModal: action.payload,
      }
    }
    case 'positions_collapsed': {
      return {
        ...state,
        collapsed: action.payload
      }
    }
    case 'loaded_single_position': {
      const { data, t } = action.payload;
      return {
        ...state,
        unsaved: false,
        isLoading: false,
        single: data,
        subMessage: setSubMessage(data.subscriptionInfo, data, t),
      };
    }
    case 'init_new_position': {
      const { t, initialLanguage } = action.payload;
      return {
        ...state,
        isLoading: false,
        unsaved: true,
        isNew: true,
        single: {
          ...initialSinglePosition(t, initialLanguage),
          title: t('page.position.new_position', 'New position'),
        },
      };
    }
    case 'add_page': {
      return {
        ...state,
        single: {
          ...state.single,
          page: action.payload,
        },
      };
    }
    case 'update_position_field': {
      const [key, value] = action.payload;

      return {
        ...state,
        unsaved: true,
        single: {
          ...state.single,
          [key]: value,
        },
        anonUpdated: key === 'anon' && value !== state.single.anon,
      };
    }
    case 'updated_position': {
      const { data, t } = action.payload;
      return {
        ...state,
        unsaved: false,
        isNew: false,
        single: {
          ...state.single,
          ...data,
          addFiles: [],
          removeFiles: [],
        },
        subMessage: setSubMessage(data?.subscriptionInfo, data, t),
      };
    }
    case 'add_mandatory': {
      const mandatoryFields = [...state.single.mandatoryFields];
      const index = mandatoryFields.indexOf(action.payload);
      if (index === -1) {
        mandatoryFields.push(action.payload);
      }
      return {
        ...state,
        unsaved: true,
        single: {
          ...state.single,
          mandatoryFields: mandatoryFields,
        },
      };
    }
    case 'toggle_position_status': {
      return {
        ...state,
        single: {
          ...state.single,
          status: action.payload,
        },
      };
    }
    case 'remove_mandatory': {
      const mandatoryFields = [...state.single.mandatoryFields];
      const index = mandatoryFields.indexOf(action.payload);
      if (index > -1) {
        mandatoryFields.splice(index, 1);
      }
      return {
        ...state,
        unsaved: true,
        single: {
          ...state.single,
          mandatoryFields: mandatoryFields,
        },
      };
    }
    case 'loaded_positions': {
      return {
        ...state,
        isLoading: false,
        positions: action.payload,
      };
    }
    case 'loaded_applications': {
      return {
        ...state,
        applications: action.payload,
      };
    }
    case 'refresh_positions': {
      return {
        ...state,
        refresher: !state.refresher,
      };
    }
    case 'update_position_status_in_all_positions': {
      const { data } = action.payload;
      const positions = state.positions.map((position) => {
        if (position.id === data.id) {
          return {
            ...position,
            status: data.status,
          };
        }
        return position;
      });
      return {
        ...state,
        positions: positions,
      };
    }
    case 'error_positions': {
      return {
        ...state,
        isLoading: false,
        isError: true,
      };
    }
    default: {
      return {
        ...state,
      };
    }
  }
};

/**
 * Return initial state to create a new page!
 * @param {string} initialLanguage position language ( en-US | de-DE etc... )
 * @returns {object} initial object to populate state!
 */
export const initialSinglePosition = (
  t,
  initialLanguage
) => {
  return {
    title: t('page.position.new_position', 'New position'),
    description: '',
    competencies: [],
    languages: [],
    binaryChoices: [],
    files: [],
    light: true,
    descriptionUrl: '',
    positionLanguage: initialLanguage || 'en-US',
    mandatoryFields: ['upload_cv', 'about'],
    anon: false,
  };
};

export const PositionProvider = ({ children }) => {

  const positionInitialState = {
    positions: [],
    single: {
      status: 'inactive', // Set the initial status here
    },
    isLoading: false,
    applications: [],
    isError: false,
    subMessage: 'Subscription status',
    refresher: false,
    unsaved: false,
    isNew: false,
    anonUpdated: false,
    count: 0,
    substatus: '',
    editPosition: false,
    createPositionModal: false,
    collapsed: false,
    appId: 0,
    refetch: false,
    candidateView: false,
    selectedApplication: null,
    isLoadingSelectedApplication: false,
    pendingSelectedApplicationUpdate: null,
  };

  const [state, reducerDispatch] = useReducer(
    positionReducer,
    positionInitialState
  );
  const selectedApplicationRequestRef = useRef(0);

  const loadSelectedApplication = useCallback(async (payload) => {
    const requestId = selectedApplicationRequestRef.current + 1;
    selectedApplicationRequestRef.current = requestId;

    reducerDispatch({
      type: 'init_selected_application',
      payload,
    });

    const request = {
      selector: payload.id,
      params: { includeSignedRequest: true },
    };

    const response = await getApplications(request);

    if (selectedApplicationRequestRef.current !== requestId) {
      return;
    }

    if (response?.success) {
      reducerDispatch({
        type: 'loaded_selected_application',
        payload: response?.data?.[0] || null,
      });
      return;
    }

    reducerDispatch({ type: 'error_selected_application' });
  }, []);

  const dispatch = useCallback(async (action) => {
    if (action.type === 'fetch_selected_application') {
      await loadSelectedApplication(action.payload);
      return;
    }

    if (
      action.type === 'select_previous_application' ||
      action.type === 'select_next_application'
    ) {
      const compactApplications = action.payload?.compactApplications;
      const currentApplication = state.selectedApplication;

      if (!currentApplication?.id || !compactApplications) {
        return;
      }

      const currentColumnKey = Object.keys(compactApplications).find((key) =>
        compactApplications[key].some((item) => item.id === currentApplication.id),
      );
      const applicationsInSubstatus = currentColumnKey
        ? compactApplications[currentColumnKey]
        : [];

      if (applicationsInSubstatus.length <= 1) {
        return;
      }

      const currentIndex = applicationsInSubstatus.findIndex(
        (application) => application.id === currentApplication.id,
      );

      if (currentIndex === -1) {
        return;
      }

      const adjacentIndex =
        action.type === 'select_previous_application'
          ? (currentIndex - 1 + applicationsInSubstatus.length) %
          applicationsInSubstatus.length
          : (currentIndex + 1) % applicationsInSubstatus.length;
      const adjacentApplication = applicationsInSubstatus[adjacentIndex];

      if (!adjacentApplication || adjacentApplication.id === currentApplication.id) {
        return;
      }

      await loadSelectedApplication({
        id: adjacentApplication.id,
        substatus: adjacentApplication.substatus,
      });
      return;
    }

    reducerDispatch(action);
  }, [loadSelectedApplication, state.selectedApplication]);

  return (
    <DispatchPositionContext.Provider value={dispatch}>
      <StatePositionContext.Provider value={state}>
        {children}
      </StatePositionContext.Provider>
    </DispatchPositionContext.Provider>
  );
};
